import {
	Avatar,
	Badge,
	HeaderActions as Actions,
	PopoverControlButton,
} from '@audio-network/componentlibrary'
import {
	AddPerson,
	Cart,
	Chat,
	Downloading,
} from '@audio-network/componentlibrary/icons'
import clsx from 'clsx'
import dynamic from 'next/dynamic'
import { useTranslation } from 'next-i18next'
import { useEffect, useMemo, useState } from 'react'

import AddToTeam from '@/components/shared/AddToTeam/AddToTeam'
import Link from '@/components/shared/Link'
import {
	hasFailedDownloadCentreMessage,
	hasInFlightDownloadCentreMessage,
} from '@/stores/downloadCentreStore'
import useDownloadCentreStore from '@/stores/downloadCentreStore'
import sessionStore from '@/stores/sessionStore'

import { useHeaderActions } from './HeaderActions.hooks'
import { useShowDownloadRoundelSuccess } from './HeaderActions.hooks'
import styles from './HeaderActions.module.css'
import { isDownloadCentreEnabled } from './HeaderActions.utils'

const ServiceMenu = dynamic(() => import('../ServiceMenu/ServiceMenu'), {
	ssr: false,
})

const ChineseServiceMenu = dynamic(
	() => import('../ChineseServiceMenu/ChineseServiceMenu'),
	{
		ssr: false,
	}
)

const DownloadCentre = dynamic(
	() => import('../DownloadCentre/DownloadCentre'),
	{
		ssr: false,
	}
)

const AccountMenu = dynamic(() => import('../AccountMenu/AccountMenu'), {
	ssr: false,
})

const LoggedOutActions = dynamic(
	() => import('../LoggedOutActions/LoggedOutActions'),
	{
		ssr: false,
	}
)

const HeaderActions = (): JSX.Element => {
	const { t } = useTranslation(['navigation'])
	const { t: tDownload } = useTranslation(['download'])
	const loadingSession = sessionStore((state) => state.loadingSession)
	const user = sessionStore((state) => state.session?.user)
	const isDownloading = useDownloadCentreStore((state) =>
		hasInFlightDownloadCentreMessage(state.messages)
	)
	const hasDownloadError = useDownloadCentreStore((state) =>
		hasFailedDownloadCentreMessage(state.messages)
	)
	const showDownloadRoundelSuccess = useShowDownloadRoundelSuccess()
	const [isDownloadCentreVisible, setIsDownloadCentreVisible] = useState(false)

	const {
		handleAddToTeamButtonClick,
		handleCartButtonClick,
		handleServiceMenuButtonClick,
		handleAccountMenuButtonClick,
		handleDownloadCentreButtonPointerDown,
		handleDownloadCentreButtonKeyDown,
		handleDownloadCentreButtonClick,
		showCNServiceMenu,
		showAddToTeamMenu,
		showServiceMenu,
		itemsCount,
	} = useHeaderActions({ user })

	const showCart = (user?.organisation.canPurchase ?? false) && itemsCount > 0

	const initials = useMemo(() => {
		if (!user) return ''
		return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`
	}, [user])

	useEffect(() => {
		setIsDownloadCentreVisible(isDownloadCentreEnabled(user))
	}, [user])

	if (loadingSession) {
		return null
	}

	if (!user) {
		return (
			<Actions id="header-actions">
				<LoggedOutActions />
			</Actions>
		)
	}

	return (
		<Actions id="header-actions">
			<div className={styles.popoverControlButtonWrapper}>
				{showAddToTeamMenu && (
					<>
						<PopoverControlButton
							aria-label={t('add-to-team-cta')}
							data-testid="add-to-team-button"
							className={clsx(styles.actionButton, styles.anchor)}
							popovertarget="add-to-team-popover"
							id="add-to-team-button"
							onClick={handleAddToTeamButtonClick}
						>
							<AddPerson />
							<span aria-hidden="true" className={styles.actionButtonText}>
								{t('add-to-team-cta')}
							</span>
						</PopoverControlButton>

						<AddToTeam />
					</>
				)}

				{showCNServiceMenu && (
					<>
						<PopoverControlButton
							aria-label={t('need-support')}
							data-testid="cn-service-menu-button"
							id="cn-service-menu-button"
							onClick={handleServiceMenuButtonClick}
							popovertarget="chinese-service-menu"
							className={clsx(styles.actionButton, styles.anchor)}
						>
							<Chat />
							<span aria-hidden="true" className={styles.actionButtonText}>
								{t('need-support')}
							</span>
						</PopoverControlButton>
						<ChineseServiceMenu />
					</>
				)}

				{showServiceMenu && (
					<>
						<PopoverControlButton
							aria-label={t('need-support')}
							data-testid="service-menu-button"
							id="service-menu-button"
							onClick={handleServiceMenuButtonClick}
							popovertarget="service-menu"
							className={clsx(styles.actionButton, styles.anchor)}
						>
							<Chat />
							<span aria-hidden="true" className={styles.actionButtonText}>
								{t('need-support')}
							</span>
						</PopoverControlButton>

						<ServiceMenu user={user} />
					</>
				)}

				{isDownloadCentreVisible && (
					<>
						<PopoverControlButton
							aria-busy={isDownloading}
							aria-label={
								hasDownloadError
									? `${tDownload('downloads')}. ${tDownload(
											'downloads-status-error'
									  )}`
									: tDownload('downloads')
							}
							className={clsx(
								styles.actionButton,
								styles.actionButtonRoundel,
								styles.downloadCentreButton
							)}
							data-testid="download-centre-button"
							id="download-centre-button"
							onClick={handleDownloadCentreButtonClick}
							onKeyDown={handleDownloadCentreButtonKeyDown}
							onPointerDown={handleDownloadCentreButtonPointerDown}
							popovertarget="download-centre"
						>
							<span
								className={clsx(
									styles.roundel,
									styles.anchor,
									showDownloadRoundelSuccess &&
										!hasDownloadError &&
										styles.roundelDownloading,
									hasDownloadError && styles.roundelFailed
								)}
								data-testid="download-centre-roundel"
							>
								<Downloading
									className={clsx(!isDownloading && styles.downloadIconIdle)}
								/>
							</span>
						</PopoverControlButton>

						<DownloadCentre />
					</>
				)}

				{showCart && (
					<Link
						aria-label={t('cart')}
						className={clsx(styles.actionButton, styles.actionButtonRoundel)}
						data-testid="cart-button"
						href="/cart"
						id="cart-button"
						onClick={handleCartButtonClick}
					>
						<Badge count={itemsCount ?? 0} data-testid="cart-badge">
							<span className={clsx(styles.roundel, styles.anchor)}>
								<Cart />
							</span>
						</Badge>
					</Link>
				)}

				<PopoverControlButton
					aria-label={t('account-actions')}
					data-testid="account-menu-button"
					className={clsx(styles.actionButton, styles.actionButtonRoundel)}
					onClick={handleAccountMenuButtonClick}
					popovertarget="account-menu"
					id="account-menu-button"
				>
					<Avatar
						aria-label={initials}
						className={styles.anchor}
						initials={initials}
					/>
				</PopoverControlButton>

				<AccountMenu user={user} />
			</div>
		</Actions>
	)
}

export default HeaderActions
